import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicPageLayout from "./PublicPageLayout";

/**
 * Public Home Page Component
 * Displays the main landing page for anonymous users
 * Uses PublicPageLayout for header/footer and same panel styling as authenticated Home
 */
const PublicHome = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleClick = (id) => {
        navigate("/Tools" + id);
    };

    return (
        <PublicPageLayout>
            <div className="container px-3 overflow-hidden">
                {/* Hero Section */}
                <div className="row text-center mb-4">
                    <div className="col-12">
                        <h1 style={{ color: '#112d4e', fontWeight: 700 }}>Welcome to Online-CADCOM</h1>
                        <p className="lead text-muted">
                            Online assisted Platform for Computer-aided design in communications
                        </p>
                        <p className="text-secondary mb-0" style={{ maxWidth: '700px', margin: '0 auto' }}>
                            {t("publicHomeDescription") || "Explore our comprehensive collection of engineering tools across multiple domains. Select a category below to browse available tools."}
                        </p>
                    </div>
                </div>

                {/* Panels - Side by Side on Desktop, Stacked on Mobile */}
                <div className="row gx-3">
                    {/* Panel 1 (Left Side on Desktop) */}
                    <div className="col-12 col-md-7 mb-2">
                        <div className="myClass p-2">
                            <div className="text-center mb-3">
                                <p className="fw-bold" style={{ color: '#112d4e' }}>{t("panel")} 1</p>
                                <img
                                    src={`${process.env.PUBLIC_URL}/assets/img/pnl_1.png`}
                                    alt="Panel 1"
                                    className="panelImage img-fluid"
                                />
                            </div>

                            <div className="row">
                                {/* Column 1 - Antenna, RF and microwave, Audio */}
                                <div className="col-12 col-md-4 d-flex flex-column gap-2 mb-2">
                                    <div className="box w-100" onClick={() => handleClick("/1/1")}>Antenna</div>
                                    <div className="box w-100" onClick={() => handleClick("/1/2")}>RF and microwave</div>
                                    <div className="box w-100" onClick={() => handleClick("/1/3")}>Audio</div>
                                </div>
                                {/* Column 2 - Analog, Interface ADC/DAC, Digital */}
                                <div className="col-12 col-md-4 d-flex flex-column gap-2 mb-2">
                                    <div className="box w-100" onClick={() => handleClick("/1/4")}>Analog</div>
                                    <div className="box w-100" onClick={() => handleClick("/1/5")}>Interface ADC/DAC</div>
                                    <div className="box w-100" onClick={() => handleClick("/1/6")}>Digital</div>
                                </div>
                                <div className="col-12 col-md-4 d-flex">
                                    <div className="bigBox1 w-100" onClick={() => handleClick("/1/7")}>Power Supply</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Panel 2 (Right Side on Desktop) */}
                    <div className="col-12 col-md-5 mb-2">
                        <div className="myClass p-2">
                            <div className="text-center mb-3">
                                <p className="fw-bold" style={{ color: '#112d4e' }}>{t("panel")} 2</p>
                                <img
                                    src={`${process.env.PUBLIC_URL}/assets/img/pnl_2.png`}
                                    alt="Panel 2"
                                    className="panelImage img-fluid"
                                />
                            </div>
                            <div className="row">
                                <div className="col-12 col-md-6 mb-2">
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <div className="box w-100" onClick={() => handleClick("/2/8")}>
                                                PCB Design
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="box w-100" onClick={() => handleClick("/2/9")}>
                                                Elements
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="box w-100" onClick={() => handleClick("/2/11")}>
                                                Nano Technology
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 d-flex flex-column">
                                    <div
                                        className="box mb-2 w-100"
                                        onClick={() => handleClick("/2/10")}
                                    >
                                        Electric Installation
                                    </div>

                                    <div className="flex-grow-1 d-flex">
                                        <div
                                            className="bigBox2 w-100"
                                            onClick={() => handleClick("/2/12")}
                                        >
                                            Outcome to Prototype:<br />
                                            FPGA / CPLD / USRPs / Arduino
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features Section */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card border-0" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="card-body text-center py-4">
                                <h5 style={{ color: '#112d4e', fontWeight: 600 }}>{t("whyUseOurPlatform") || "Why Use Our Platform?"}</h5>
                                <div className="row mt-3">
                                    <div className="col-md-4 mb-3 mb-md-0">
                                        <div className="p-3">
                                            <i className="bi bi-collection" style={{ fontSize: '2rem', color: '#112d4e' }}></i>
                                            <h6 className="mt-2" style={{ color: '#112d4e' }}>{t("comprehensiveCatalog") || "Comprehensive Catalog"}</h6>
                                            <p className="text-muted small mb-0">{t("comprehensiveCatalogDesc") || "Access a wide range of engineering tools across multiple domains"}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3 mb-md-0">
                                        <div className="p-3">
                                            <i className="bi bi-graph-up-arrow" style={{ fontSize: '2rem', color: '#112d4e' }}></i>
                                            <h6 className="mt-2" style={{ color: '#112d4e' }}>{t("smartSelection") || "Smart Selection"}</h6>
                                            <p className="text-muted small mb-0">{t("smartSelectionDesc") || "Use multi-criteria decision methods to find the best tool"}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="p-3">
                                            <i className="bi bi-shield-check" style={{ fontSize: '2rem', color: '#112d4e' }}></i>
                                            <h6 className="mt-2" style={{ color: '#112d4e' }}>{t("trustedInfo") || "Trusted Information"}</h6>
                                            <p className="text-muted small mb-0">{t("trustedInfoDesc") || "All tools are verified and approved by experts"}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PublicPageLayout>
    );
};

export default PublicHome;
